import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { BookOpen, Plus, Edit, Trash2, Users, CheckCircle, Award } from 'lucide-react';

/**
 * TeacherHomeworkSection — list assignments, create new, view & grade submissions.
 * Self-contained: fetches everything it needs (templates, assignments, submissions).
 */
export const TeacherHomeworkSection = () => {
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ template_id: '', topic_id: '', title: '', instructions_html: '', max_score: 100, due_offset_days: 7 });
  const [saving, setSaving] = useState(false);

  // submissions viewer
  const [subsDialog, setSubsDialog] = useState(false);
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [gradeMap, setGradeMap] = useState({}); // submission_id -> { score, feedback }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, asnRes] = await Promise.all([
        api.get('/teacher/curriculum-templates'),
        api.get('/teacher/homework'),
      ]);
      setTemplates(tplRes.data || []);
      setAssignments(asnRes.data || []);
    } catch (e) {
      toast.error('Failed to load homework');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const selectedTemplate = templates.find(t => t.id === form.template_id);
  const topicChoices = selectedTemplate?.topics || [];

  const openCreate = () => {
    setEditing(null);
    setForm({ template_id: '', topic_id: '', title: '', instructions_html: '', max_score: 100, due_offset_days: 7 });
    setDialogOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      template_id: a.template_id || '',
      topic_id: a.topic_id || '',
      title: a.title || '',
      instructions_html: a.instructions_html || '',
      max_score: a.max_score || 100,
      due_offset_days: a.due_offset_days ?? 7,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('Title required');
    if (!editing && (!form.template_id || !form.topic_id)) return toast.error('Pick a template and topic');
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/teacher/homework/${editing.id}`, {
          title: form.title, instructions_html: form.instructions_html,
          max_score: parseInt(form.max_score) || 100,
          due_offset_days: form.due_offset_days === '' ? null : parseInt(form.due_offset_days),
        });
        toast.success('Assignment updated');
      } else {
        await api.post('/teacher/homework', {
          template_id: form.template_id,
          topic_id: form.topic_id,
          title: form.title.trim(),
          instructions_html: form.instructions_html,
          max_score: parseInt(form.max_score) || 100,
          due_offset_days: form.due_offset_days === '' ? null : parseInt(form.due_offset_days),
        });
        toast.success('Assignment published');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Archive this assignment? Students will no longer see it.')) return;
    try {
      await api.delete(`/teacher/homework/${id}`);
      toast.success('Archived');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const openSubmissions = async (a) => {
    setActiveAssignment(a);
    setSubsDialog(true);
    try {
      const res = await api.get(`/teacher/homework/${a.id}/submissions`);
      setSubmissions(res.data || []);
      const map = {};
      (res.data || []).forEach(s => { map[s.id] = { score: s.score ?? '', feedback: s.feedback || '' }; });
      setGradeMap(map);
    } catch { toast.error('Failed to load submissions'); }
  };

  const handleGrade = async (sub) => {
    const g = gradeMap[sub.id] || {};
    const score = parseInt(g.score);
    if (isNaN(score)) return toast.error('Enter a numeric score');
    try {
      await api.put(`/teacher/homework/submissions/${sub.id}/grade`, { score, feedback: g.feedback || '' });
      toast.success(`Graded ${sub.student_name}`);
      openSubmissions(activeAssignment); // refresh
      fetchData(); // refresh assignment counters
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grade failed');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-blue-600" /> Homework
          </h1>
          <p className="text-slate-500 font-semibold">Create and grade homework assignments tied to curriculum topics</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 font-bold" onClick={openCreate} data-testid="create-homework-btn">
          <Plus className="w-4 h-4 mr-1" /> New Assignment
        </Button>
      </div>

      {loading ? (
        <div className="text-center text-slate-500 py-12">Loading…</div>
      ) : assignments.length === 0 ? (
        <div className="nb-card text-center py-12">
          <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-semibold">No assignments yet</p>
          <p className="text-xs text-slate-400 mt-1">Click "New Assignment" to create your first piece of homework.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assignments.map(a => (
            <div key={a.id} className="nb-card p-5" data-testid={`assignment-card-${a.id}`}>
              <div className="flex items-start justify-between mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">{a.template_title || a.template_id?.slice(0, 8)}</span>
                <span className="text-[10px] font-bold uppercase tracking-wide text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">{a.topic_title}</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">{a.title}</h3>
              <div className="text-xs text-slate-500 line-clamp-2 mb-3" dangerouslySetInnerHTML={{ __html: a.instructions_html || '<i>No instructions</i>' }} />
              <div className="flex items-center gap-2 text-xs text-slate-600 mb-3">
                <Award className="w-3.5 h-3.5 text-amber-500" /> {a.max_score} pts
                {a.due_offset_days != null && <span className="text-slate-400">• Due +{a.due_offset_days}d</span>}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div className="bg-blue-100 text-blue-700 rounded-lg p-2 text-center font-bold">
                  <Users className="w-3.5 h-3.5 inline mr-1" /> {a.submission_count || 0} submitted
                </div>
                <div className="bg-emerald-100 text-emerald-700 rounded-lg p-2 text-center font-bold">
                  <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> {a.graded_count || 0} graded
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-xs font-bold" onClick={() => openSubmissions(a)} data-testid={`view-subs-${a.id}`}>
                  View & Grade
                </Button>
                <Button size="sm" variant="outline" className="border-amber-400 text-amber-700" onClick={() => openEdit(a)} data-testid={`edit-hw-${a.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="outline" className="border-red-400 text-red-700" onClick={() => handleDelete(a.id)} data-testid={`delete-hw-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold">{editing ? 'Edit Assignment' : 'New Homework Assignment'}</DialogTitle>
            <DialogDescription>{editing ? 'Update the assignment details.' : 'Tie this assignment to a curriculum topic.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-bold">Curriculum *</Label>
                  <Select value={form.template_id} onValueChange={v => setForm(p => ({ ...p, template_id: v, topic_id: '' }))}>
                    <SelectTrigger className="mt-1" data-testid="hw-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.title || t.vertical}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-bold">Topic *</Label>
                  <Select value={form.topic_id} onValueChange={v => setForm(p => ({ ...p, topic_id: v }))} disabled={!form.template_id}>
                    <SelectTrigger className="mt-1" data-testid="hw-topic"><SelectValue placeholder={form.template_id ? "Pick a topic" : "Select template first"} /></SelectTrigger>
                    <SelectContent>
                      {topicChoices.map(t => <SelectItem key={t.id} value={t.id}>{t.title || t.name || `Topic ${t.id?.slice(0, 6)}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div>
              <Label className="font-bold">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Build a Python calculator" className="mt-1" data-testid="hw-title" />
            </div>
            <div>
              <Label className="font-bold">Instructions</Label>
              <Textarea value={form.instructions_html} onChange={e => setForm(p => ({ ...p, instructions_html: e.target.value }))}
                placeholder="What should the student do? HTML allowed."
                className="mt-1 min-h-[120px]" data-testid="hw-instructions" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-bold">Max score</Label>
                <Input type="number" min="1" value={form.max_score} onChange={e => setForm(p => ({ ...p, max_score: e.target.value }))} className="mt-1" data-testid="hw-max-score" />
              </div>
              <div>
                <Label className="font-bold">Due (days after assignment)</Label>
                <Input type="number" min="0" value={form.due_offset_days ?? ''} onChange={e => setForm(p => ({ ...p, due_offset_days: e.target.value }))} placeholder="leave blank for no deadline" className="mt-1" data-testid="hw-due-days" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 font-bold" data-testid="save-hw-btn">
                {saving ? 'Saving…' : (editing ? 'Update' : 'Publish')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submissions Dialog */}
      <Dialog open={subsDialog} onOpenChange={setSubsDialog}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold">Submissions — {activeAssignment?.title}</DialogTitle>
            <DialogDescription>Grade student work. Max score: {activeAssignment?.max_score}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {submissions.length === 0 ? (
              <p className="text-slate-500 text-center py-6">No submissions yet.</p>
            ) : submissions.map(s => (
              <div key={s.id} className="border-2 border-slate-200 rounded-xl p-4" data-testid={`submission-${s.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-bold text-slate-900">{s.student_name}</h4>
                    <p className="text-xs text-slate-500">Submitted {new Date(s.submitted_at).toLocaleString()} • Attempt {s.attempt}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${s.status === 'graded' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {s.status === 'graded' ? `Graded: ${s.score}/${activeAssignment?.max_score}` : 'Pending'}
                  </span>
                </div>
                {s.text_answer && (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 mb-2 whitespace-pre-wrap">{s.text_answer}</div>
                )}
                {(s.files || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {s.files.map((f, i) => (
                      <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                        className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded hover:bg-blue-200">
                        📎 {f.original_name || f.filename}
                      </a>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2 items-end">
                  <div>
                    <Label className="text-xs font-bold">Score</Label>
                    <Input type="number" min="0" max={activeAssignment?.max_score}
                      value={gradeMap[s.id]?.score ?? ''}
                      onChange={e => setGradeMap(p => ({ ...p, [s.id]: { ...p[s.id], score: e.target.value } }))}
                      className="mt-1" data-testid={`grade-score-${s.id}`} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs font-bold">Feedback</Label>
                    <Input value={gradeMap[s.id]?.feedback ?? ''}
                      onChange={e => setGradeMap(p => ({ ...p, [s.id]: { ...p[s.id], feedback: e.target.value } }))}
                      placeholder="Quick feedback for the student" className="mt-1" data-testid={`grade-feedback-${s.id}`} />
                  </div>
                </div>
                <Button size="sm" className="mt-2 bg-emerald-600 hover:bg-emerald-700 font-bold" onClick={() => handleGrade(s)} data-testid={`save-grade-${s.id}`}>
                  Save Grade
                </Button>
                {s.feedback && s.status === 'graded' && (
                  <div className="mt-2 text-xs text-emerald-700 italic">Last feedback: "{s.feedback}"</div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeacherHomeworkSection;
